import { useState } from 'react'
import {
  Smile,
  Frown,
  Meh,
  TrendingUp,
  MessageSquare,
  BarChart3,
  AlertTriangle,
  Zap,
  Target,
  Users,
  Brain,
  Lightbulb,
  CheckCircle,
  TrendingDown,
  Clock,
  Filter,
  Download,
  Linkedin,
  Mail,
  MessageCircle,
} from 'lucide-react'

export default function SentimentAnalysisPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedChannel, setSelectedChannel] = useState('all')
  const [timeRange, setTimeRange] = useState('30d')

  const stats = {
    totalFeedback: 2847,
    avgSentiment: 72.4,
    positiveFeedback: 1847,
    negativeFeedback: 456,
    neutralFeedback: 544,
    trendChange: '+5.2%',
    pivotOpportunities: 8,
  }

  const sentimentByChannel = [
    {
      channel: 'Discord',
      icon: MessageCircle,
      color: 'from-indigo-600 to-indigo-500',
      positive: 68,
      neutral: 22,
      negative: 10,
      messages: 1240,
      sentiment: 72,
    },
    {
      channel: 'LinkedIn',
      icon: Linkedin,
      color: 'from-blue-600 to-blue-500',
      positive: 85,
      neutral: 12,
      negative: 3,
      messages: 456,
      sentiment: 84,
    },
    {
      channel: 'Email',
      icon: Mail,
      color: 'from-orange-600 to-orange-500',
      positive: 62,
      neutral: 28,
      negative: 10,
      messages: 1151,
      sentiment: 68,
    },
  ]

  const sentimentKeywords = [
    { keyword: 'Easy to use', sentiment: 'Positive', mentions: 847, trend: 'up' },
    { keyword: 'Powerful features', sentiment: 'Positive', mentions: 623, trend: 'up' },
    { keyword: 'Great support', sentiment: 'Positive', mentions: 456, trend: 'stable' },
    { keyword: 'Pricing concerns', sentiment: 'Negative', mentions: 234, trend: 'down' },
    { keyword: 'Needs improvement', sentiment: 'Negative', mentions: 156, trend: 'up' },
    { keyword: 'Integration issues', sentiment: 'Negative', mentions: 126, trend: 'down' },
  ]

  const productPivots = [
    {
      id: 1,
      title: 'Enhanced Mobile Experience',
      sentiment: 'Positive Pivot',
      description: '847 mentions of mobile usage difficulty. Market shows strong demand for native mobile apps',
      confidence: 94,
      impact: 'High',
      affectedUsers: '2,300+',
      timeline: '2-3 months',
      effort: 'Medium',
    },
    {
      id: 2,
      title: 'Simplified Pricing Model',
      sentiment: 'Negative Pain Point',
      description: 'Pricing concerns mentioned in 234 feedback entries. Competitors offer more transparent pricing',
      confidence: 89,
      impact: 'High',
      affectedUsers: '1,800+',
      timeline: '1 month',
      effort: 'Low',
    },
    {
      id: 3,
      title: 'Industry-Specific Templates',
      sentiment: 'Feature Request',
      description: '612 requests for pre-built templates for healthcare, finance, and SaaS industries',
      confidence: 87,
      impact: 'Medium',
      affectedUsers: '1,500+',
      timeline: '1-2 months',
      effort: 'Medium',
    },
    {
      id: 4,
      title: 'Advanced Analytics Dashboard',
      sentiment: 'Feature Gap',
      description: '456 power users requesting deeper analytics and custom reporting capabilities',
      confidence: 85,
      impact: 'Medium',
      affectedUsers: '900+',
      timeline: '3-4 months',
      effort: 'High',
    },
    {
      id: 5,
      title: 'API Rate Limit Increases',
      sentiment: 'Integration Pain Point',
      description: '156 developers report API limits restricting development. Easy win to improve developer experience',
      confidence: 92,
      impact: 'Medium',
      affectedUsers: '340+',
      timeline: '2 weeks',
      effort: 'Low',
    },
    {
      id: 6,
      title: 'White-Label Solution',
      sentiment: 'Enterprise Demand',
      description: '234 enterprise customers request white-label options for reselling and internal use',
      confidence: 88,
      impact: 'High',
      affectedUsers: '234+',
      timeline: '2-3 months',
      effort: 'High',
    },
    {
      id: 7,
      title: '24/7 Live Support Tier',
      sentiment: 'Service Expectation',
      description: 'Support response time mentioned as pain point in 189 feedback entries from enterprise segment',
      confidence: 86,
      impact: 'Medium',
      affectedUsers: '450+',
      timeline: '1-2 months',
      effort: 'Medium',
    },
    {
      id: 8,
      title: 'Multi-Language Support',
      sentiment: 'Global Expansion',
      description: 'Strong demand for Chinese, Spanish, and German interfaces. 312 non-English requests detected',
      confidence: 83,
      impact: 'Medium',
      affectedUsers: '600+',
      timeline: '2-3 months',
      effort: 'Medium',
    },
  ]

  const recentSentiment = [
    {
      author: 'Sarah Johnson',
      channel: 'LinkedIn',
      message: 'Just migrated to your platform and the ease of integration is incredible. Finally found a solution that just works!',
      sentiment: 'Highly Positive',
      score: 95,
      date: '2024-04-25',
      tags: ['Easy to use', 'Integration'],
    },
    {
      author: 'Michael Chen',
      channel: 'Discord',
      message: 'Love the new dashboard features, but the mobile app is really buggy on my iPhone 15. Hope you can fix it soon',
      sentiment: 'Mixed',
      score: 62,
      date: '2024-04-25',
      tags: ['Mobile issues', 'UI/UX'],
    },
    {
      author: 'Jessica Martinez',
      channel: 'Email',
      message: 'Your pricing is getting too expensive compared to X and Y. Considering alternatives for next year if you don\'t address this',
      sentiment: 'Negative',
      score: 28,
      date: '2024-04-24',
      tags: ['Pricing concerns', 'Churn risk'],
    },
    {
      author: 'David Kumar',
      channel: 'Discord',
      message: 'The API documentation is comprehensive but we hit rate limits constantly. Would pay extra for higher limits',
      sentiment: 'Constructive',
      score: 58,
      date: '2024-04-24',
      tags: ['API limitations', 'Developer experience'],
    },
    {
      author: 'Emma Thompson',
      channel: 'LinkedIn',
      message: 'Been using this for 3 years and it keeps getting better. The team really listens to their users. Highly recommend!',
      sentiment: 'Highly Positive',
      score: 92,
      date: '2024-04-23',
      tags: ['Product quality', 'Customer care'],
    },
  ]

  const sentimentTrends = [
    { week: 'Week 1', positive: 68, neutral: 22, negative: 10 },
    { week: 'Week 2', positive: 70, neutral: 20, negative: 10 },
    { week: 'Week 3', positive: 72, neutral: 18, negative: 10 },
    { week: 'Week 4', positive: 75, neutral: 17, negative: 8 },
    { week: 'Week 5', positive: 77, neutral: 16, negative: 7 },
  ]

  const features = [
    {
      icon: Brain,
      title: 'Sentiment AI Engine',
      description: 'Advanced NLP-powered sentiment analysis with contextual understanding and sarcasm detection',
    },
    {
      icon: MessageSquare,
      title: 'Multi-Channel Aggregation',
      description: 'Unified analysis across Discord, LinkedIn, Email, and social media platforms in one dashboard',
    },
    {
      icon: Target,
      title: 'Product Pivot Detection',
      description: 'Automatic identification of feature requests, pain points, and market opportunities',
    },
    {
      icon: Users,
      title: 'User Segment Analysis',
      description: 'Sentiment tracking by user segment, company size, industry, and customer lifetime value',
    },
    {
      icon: TrendingUp,
      title: 'Trend Forecasting',
      description: 'Predictive analytics showing sentiment trends and churn risk indicators',
    },
    {
      icon: Lightbulb,
      title: 'Actionable Insights',
      description: 'AI-generated recommendations for product improvements based on customer feedback',
    },
    {
      icon: AlertTriangle,
      title: 'Churn Alert System',
      description: 'Real-time alerts for high-risk customers showing signs of switching to competitors',
    },
    {
      icon: BarChart3,
      title: 'Competitive Intelligence',
      description: 'Track competitor mentions and sentiment to identify market positioning opportunities',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Sentiment Analysis Engine</h1>
            <p className="text-slate-400">AI-Powered Customer Feedback & Product Pivot Intelligence</p>
          </div>
          <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all">
            <Download size={20} className="inline mr-2" />
            Export Report
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Feedback</p>
                <p className="text-2xl font-black text-white">{stats.totalFeedback}</p>
              </div>
              <MessageSquare size={32} className="text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Sentiment</p>
                <p className="text-2xl font-black text-white">{stats.avgSentiment}%</p>
              </div>
              <Smile size={32} className="text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Positive</p>
                <p className="text-2xl font-black text-green-400">{stats.positiveFeedback}</p>
              </div>
              <CheckCircle size={32} className="text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Negative</p>
                <p className="text-2xl font-black text-red-400">{stats.negativeFeedback}</p>
              </div>
              <Frown size={32} className="text-red-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Trend</p>
                <p className="text-2xl font-black text-white">{stats.trendChange}</p>
              </div>
              <TrendingUp size={32} className="text-cyan-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pivot Ideas</p>
                <p className="text-2xl font-black text-white">{stats.pivotOpportunities}</p>
              </div>
              <Lightbulb size={32} className="text-orange-400 opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Channel Selector and Tabs */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'channels', label: 'By Channel', icon: MessageSquare },
            { id: 'pivots', label: 'Product Pivots', icon: Target },
            { id: 'recent', label: 'Recent Feedback', icon: MessageCircle },
            { id: 'trends', label: 'Trends', icon: TrendingUp },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600/40 to-cyan-600/40 text-blue-200 border border-blue-500/50'
                  : 'text-slate-400 hover:text-blue-300'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Features Grid */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">AI Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-500/40 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <feature.icon size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sentiment Keywords */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Top Keywords by Sentiment</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sentimentKeywords.map((item, idx) => (
                <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-white">{item.keyword}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      item.sentiment === 'Positive'
                        ? 'bg-green-600/20 text-green-300'
                        : 'bg-red-600/20 text-red-300'
                    }`}>
                      {item.sentiment}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">{item.mentions} mentions</span>
                    {item.trend === 'up' ? (
                      <TrendingUp size={16} className="text-green-400" />
                    ) : item.trend === 'down' ? (
                      <TrendingDown size={16} className="text-red-400" />
                    ) : (
                      <span className="text-xs text-slate-400">Stable</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Channels Tab */}
      {activeTab === 'channels' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {sentimentByChannel.map(ch => {
            const ChannelIcon = ch.icon
            return (
              <div key={ch.channel} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-3 bg-gradient-to-br ${ch.color} rounded-lg`}>
                    <ChannelIcon size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{ch.channel}</h3>
                    <p className="text-sm text-slate-400">{ch.messages} messages analyzed</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-semibold text-green-400">Positive: {ch.positive}%</span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${ch.positive}%` }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-400">Neutral: {ch.neutral}%</span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div className="bg-slate-400 h-2 rounded-full" style={{ width: `${ch.neutral}%` }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-semibold text-red-400">Negative: {ch.negative}%</span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: `${ch.negative}%` }}></div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-700">
                    <p className="text-2xl font-black text-white">{ch.sentiment}%</p>
                    <p className="text-xs text-slate-400">Overall Sentiment Score</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Product Pivots Tab */}
      {activeTab === 'pivots' && (
        <div className="space-y-4">
          {productPivots.map(pivot => (
            <div key={pivot.id} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-white">{pivot.title}</h3>
                    <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                      pivot.sentiment === 'Positive Pivot'
                        ? 'bg-green-600/20 text-green-300'
                        : pivot.sentiment === 'Negative Pain Point'
                        ? 'bg-red-600/20 text-red-300'
                        : 'bg-blue-600/20 text-blue-300'
                    }`}>
                      {pivot.sentiment}
                    </span>
                  </div>
                  <p className="text-slate-400">{pivot.description}</p>
                </div>
                <span className={`text-2xl font-black ml-4 ${
                  pivot.confidence >= 90 ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {pivot.confidence}%
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-slate-700 text-sm">
                <div>
                  <p className="text-slate-400">Impact</p>
                  <p className="font-semibold text-white">{pivot.impact}</p>
                </div>
                <div>
                  <p className="text-slate-400">Users Affected</p>
                  <p className="font-semibold text-white">{pivot.affectedUsers}</p>
                </div>
                <div>
                  <p className="text-slate-400">Timeline</p>
                  <p className="font-semibold text-white">{pivot.timeline}</p>
                </div>
                <div>
                  <p className="text-slate-400">Effort</p>
                  <p className="font-semibold text-white">{pivot.effort}</p>
                </div>
                <div className="text-right">
                  <button className="px-3 py-1 bg-blue-600/20 text-blue-300 text-xs rounded hover:bg-blue-600/30 transition-all">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Feedback Tab */}
      {activeTab === 'recent' && (
        <div className="space-y-4">
          {recentSentiment.map((feedback, idx) => (
            <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-white">{feedback.author}</h3>
                    <span className="text-xs px-2 py-1 bg-slate-700/50 text-slate-300 rounded">
                      {feedback.channel}
                    </span>
                  </div>
                  <p className="text-slate-300 mb-2">{feedback.message}</p>
                  <div className="flex gap-2 flex-wrap">
                    {feedback.tags.map(tag => (
                      <span key={tag} className="text-xs bg-blue-600/20 text-blue-300 px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right ml-4">
                  <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap block mb-2 ${
                    feedback.sentiment === 'Highly Positive'
                      ? 'bg-green-600/20 text-green-300'
                      : feedback.sentiment === 'Negative'
                      ? 'bg-red-600/20 text-red-300'
                      : 'bg-yellow-600/20 text-yellow-300'
                  }`}>
                    {feedback.sentiment}
                  </span>
                  <p className="text-2xl font-black text-white">{feedback.score}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">{feedback.date}</p>
            </div>
          ))}
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <div>
          <h2 className="text-2xl font-black text-white mb-6">30-Day Sentiment Trend</h2>
          <div className="bg-slate-800/50 p-8 rounded-lg border border-blue-500/20">
            <div className="grid grid-cols-5 gap-4">
              {sentimentTrends.map((trend, idx) => (
                <div key={idx} className="text-center">
                  <div className="relative h-32 mb-4">
                    <div className="absolute inset-0 flex items-end justify-center gap-1">
                      <div className="w-1/3 bg-green-500 rounded-t" style={{ height: `${trend.positive}%` }}></div>
                      <div className="w-1/3 bg-slate-400 rounded-t" style={{ height: `${trend.neutral}%` }}></div>
                      <div className="w-1/3 bg-red-500 rounded-t" style={{ height: `${trend.negative}%` }}></div>
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-white">{trend.week}</h3>
                  <p className="text-xs text-slate-400 mt-2">
                    <span className="text-green-400">{trend.positive}%</span> / <span className="text-slate-400">{trend.neutral}%</span> / <span className="text-red-400">{trend.negative}%</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
